// ────────────────────────────────────────────────────────────────────────────
// The product editor's draft, and the conversions in and out of it.
//
// The ADR row and the waste codes are held as SELECTIONS — enough of the row
// to show the operator what they picked — but only the key / codes go on the
// wire. The server copies the rest from its own catalogues, so nothing typed
// or cached here can end up stored against the product.
// ────────────────────────────────────────────────────────────────────────────
import type {
  AdrCatalogueEntry,
  BaseStatusValue,
  CreateProductRequest,
  EwcCatalogueEntry,
  Product,
  ProductGradeValue,
  UpdateProductRequest,
} from '@my/directory-api';

/** What the editor shows for a picked ADR row. */
export interface AdrSelection {
  key: string;
  un: string;
  name: string;
  class: string;
  packingGroup: string;
  labels: string;
  specialProvisions: string;
  hazardNumber: string;
  tunnelCode: string;
  transportCategory: string;
  /** "ADR 2025" — null only for a selection the server has not stamped yet. */
  edition: string | null;
}

/** What the editor shows for a picked waste code. */
export interface EwcSelection {
  code: string;
  description: string;
  hazardous: boolean;
}

export interface ProductDraft {
  name: string;
  code: string;
  grade: ProductGradeValue;
  /** Text while typing; parsed on save. Accepts a decimal comma. */
  density: string;
  notes: string;
  status: BaseStatusValue;
  adr: AdrSelection | null;
  adrTechnicalName: string;
  /** The waste codes, in display order. Empty = not waste. */
  ewc: EwcSelection[];
  /** Which of `ewc` a new cargo row takes; null = the first. */
  ewcDefault: string | null;
}

export const GRADE_VALUES: ProductGradeValue[] = ['None', 'Food', 'Feed'];

export function emptyDraft(): ProductDraft {
  return {
    name: '',
    code: '',
    grade: 'None',
    density: '',
    notes: '',
    status: 'Active',
    adr: null,
    adrTechnicalName: '',
    ewc: [],
    ewcDefault: null,
  };
}

/** A stored product → the editor. The ADR selection is the product's own copy. */
export function toDraft(p: Product): ProductDraft {
  return {
    name: p.name,
    code: p.code ?? '',
    grade: p.grade,
    density: p.densityKgPerL == null ? '' : String(p.densityKgPerL),
    notes: p.notes ?? '',
    status: p.status,
    adr: p.adrKey
      ? {
          key: p.adrKey,
          un: p.adrUnNumber ?? '',
          name: p.adrProperShippingName ?? '',
          class: p.adrClass ?? '',
          packingGroup: p.adrPackingGroup ?? '',
          labels: p.adrLabels ?? '',
          specialProvisions: p.adrSpecialProvisions ?? '',
          hazardNumber: p.adrHazardNumber ?? '',
          tunnelCode: p.adrTunnelCode ?? '',
          transportCategory: p.adrTransportCategory ?? '',
          edition: p.adrEdition,
        }
      : null,
    adrTechnicalName: p.adrTechnicalName ?? '',
    ewc: (p.wasteCodes ?? []).map((w) => ({
      code: w.code,
      description: w.description,
      hazardous: w.hazardous,
    })),
    ewcDefault: p.wasteCodes?.find((w) => w.isDefault)?.code ?? null,
  };
}

export function adrFromCatalogue(entry: AdrCatalogueEntry, edition: string): AdrSelection {
  return {
    key: entry.key,
    un: entry.un,
    name: entry.name,
    class: entry.class,
    packingGroup: entry.packingGroup,
    labels: entry.labels,
    specialProvisions: entry.specialProvisions,
    hazardNumber: entry.hazardNumber,
    tunnelCode: entry.tunnelCode,
    transportCategory: entry.transportCategory,
    edition,
  };
}

export function ewcFromCatalogue(entry: EwcCatalogueEntry): EwcSelection {
  return { code: entry.code, description: entry.description, hazardous: entry.hazardous };
}

/**
 * ADR special provision 274: the shipping name must be followed by the
 * technical name — mostly the "N.O.S." entries ("FLAMMABLE LIQUID, N.O.S.
 * (contains xylene)"). Same rule the server uses for `adrTechnicalNameRequired`.
 */
export function needsTechnicalName(adr: AdrSelection | null): boolean {
  return !!adr && adr.specialProvisions.split(/\s+/).includes('274');
}

/** `""` → null; `"1,84"` / `"1.84"` → 1.84; anything else → NaN. */
export function parseDensity(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  return Number(trimmed.replace(',', '.'));
}

export interface DraftErrors {
  name: boolean;
  density: boolean;
}

/** Mirrors the server validator: name required, density > 0 and < 100 when given. */
export function validateDraft(d: ProductDraft): DraftErrors {
  const density = parseDensity(d.density);
  return {
    name: !d.name.trim(),
    density: density !== null && (!Number.isFinite(density) || density <= 0 || density >= 100),
  };
}

export function hasErrors(e: DraftErrors): boolean {
  return e.name || e.density;
}

function blankToNull(s: string): string | null {
  const trimmed = s.trim();
  return trimmed ? trimmed : null;
}

/** The fields create and update share. The editor renders every one of them. */
function body(d: ProductDraft) {
  return {
    name: d.name.trim(),
    code: blankToNull(d.code),
    grade: d.grade,
    densityKgPerL: parseDensity(d.density),
    notes: blankToNull(d.notes),
    status: d.status,
    adrKey: d.adr?.key ?? null,
    // Only meaningful beside a row; the server refuses one without.
    adrTechnicalName: d.adr ? blankToNull(d.adrTechnicalName) : null,
    ewcCodes: d.ewc.map((w) => w.code),
    defaultEwcCode: defaultEwc(d),
  };
}

/** The default waste code: the chosen one while it is still listed, else the first. */
export function defaultEwc(d: Pick<ProductDraft, 'ewc' | 'ewcDefault'>): string | null {
  if (d.ewcDefault && d.ewc.some((w) => w.code === d.ewcDefault)) return d.ewcDefault;
  return d.ewc[0]?.code ?? null;
}

export function toCreateRequest(organisationId: string, d: ProductDraft): CreateProductRequest {
  return { organisationId, ...body(d) };
}

export function toUpdateRequest(productId: string, d: ProductDraft): UpdateProductRequest {
  return { productId, ...body(d) };
}
